import {Component, HostListener, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {TenantsService} from '../../../services/tenants.service';
import {MessageService} from 'primeng/api';
import {Tenant} from '../../../model/tenant';
import {Organization} from '../../../model/organization';
import {noSpecialCharacters, noWhitespaces} from '../../../shared/custom-validators';
import {ComponentCanDeactivate} from '../../../common/can-deactivate/component-can-deactivate';
import {AuthGuard} from '../../../common/auth-guard.service';
import {LoggedUser} from '../../../common/logged-user.service';
import {CommonMessagesService} from '../../../services/common-messages.service';
import {CepRule, SimpleCepRule} from "../../../model/cep-rule";
import {CepRulesService} from "../../../services/cep-rules.service";
import {CepRulePersistRequest} from "../../../model/requests/cep-rule-persist-request";
import {OrganizationsService} from "../../../services/organizations.service";
import {SelectedEntity} from "../../../model/selected-entity";
import {EntitiesAndSensorIdentifier} from "../../../model/entities-and-sensor-identifier";
import {HttpStatusCode} from "@angular/common/http"

@Component({
  selector: 'app-cep-rules-form',
  templateUrl: './cep-rules-form.component.html',
  styleUrls: ['./cep-rules-form.component.scss']
})
export class CepRulesFormComponent implements OnInit, ComponentCanDeactivate {

  cepRule: CepRule;
  simpleRules: SimpleCepRule[];
  selectedRule: SimpleCepRule;

  tenants: Tenant[];
  organizations: Organization[];

  aggregatorFunctionTypes: string[];
  limitOperatorTypes: string[];
  transformationTypes: string[];

  cepRuleForm: FormGroup;
  persistingChanges: any;
  formSuccess: boolean;

  displayEntityMeasurementSelector: boolean;
  selectedEntities: SelectedEntity[];
  loadingSelected: boolean;
  selectedFirst: number;
  selectedRows: number;

  indexName: number;
  hasBeenOpened: boolean;

  mode: 'create' | 'edit';

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private tenantsService: TenantsService,
              private organizationsService: OrganizationsService,
              private messageService: MessageService,
              private authGuard: AuthGuard,
              private loggedUser: LoggedUser,
              private cepRulesService: CepRulesService,
              private commonMessagesService: CommonMessagesService) {
    this.simpleRules = [];
    this.formSuccess = false;
    this.aggregatorFunctionTypes = ['SUM', 'AVG', 'MIN', 'MAX', 'DIFF', 'INCR', 'DECR'].sort();
    this.limitOperatorTypes = ['EQUAL', 'NOT_EQUAL', 'GREATER_EQUAL', 'LESS_EQUAL', 'GREATER', 'LESS'].sort();
    this.transformationTypes = ['BYTE_MASK'];

    this.displayEntityMeasurementSelector = false;
    this.selectedEntities = [];
    this.selectedFirst = 0;
    this.selectedRows = 5;
    this.loadingSelected = false;
    this.hasBeenOpened = false;
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path, urlSegment[2].path);
      }
    });
  }

  loadCreationForm(): void {
    this.cepRule = new CepRule();
    this.indexName = 1;
    this.loadTenants();
  }

  loadEditionForm(tenantUuid, organization, nameComplexRule): void {
    const organizationUuid = organization === 'global' ? null : organization;

    this.tenantsService.getTenantCepRule(tenantUuid, organizationUuid, nameComplexRule).toPromise()
      .then(response => {
        this.cepRule = response.cepRule;
        this.simpleRules = response.cepRule.simpleRules;
        let nameLastSimpleRule = this.simpleRules[this.simpleRules.length-1].name;
        this.indexName = parseInt(nameLastSimpleRule.substr(nameLastSimpleRule.length-1)) + 1;
        this.tenants = [this.cepRule.tenant];
        if (this.cepRule.organization) {
          this.organizations = [this.cepRule.organization];
        } else {
          this.organizations = [];
        }
        this.createForm(this.cepRule);
        this.selectRules(this.simpleRules);
      }, error => {
        if (error.status === HttpStatusCode.NotFound) {
          this.router.navigate(['/not-found']);
        }
      });
  }

  loadTenants(): void {
    Promise.all([
      this.tenantsService.getTenantSummaries().toPromise(),
    ]).then(response => {
      this.tenants = response[0].tenants;
      if (!this.authGuard.superAdminRequired()) {
        this.cepRule.tenant = this.tenants[0];
      }
      this.createForm(this.cepRule);
    });
  }

  private createForm(cepRule: CepRule): void {
    this.cepRuleForm = this.formBuilder.group({
      'nameComplexRule': [{value: cepRule.nameComplexRule, disabled: this.mode === 'edit'}, [Validators.maxLength(255), Validators.required, noSpecialCharacters, noWhitespaces]],
      'tenant': [{value: cepRule.tenant, disabled: this.mode === 'edit'}, [Validators.required]],
      'organization': [{value: cepRule.organization, disabled: true}]
    });
    this.cepRuleForm.markAsPending();

    if( this.tenants.length === 1 ) {
      this.cepRuleForm.get('tenant').disable();
    }

    if (this.cepRule.tenant && this.mode === 'create') {
      this.loadOrganizations(this.cepRule.tenant.uuid).then(() => {
        if( this.tenants.length === 1 && this.organizations.length === 1 ) {
          this.cepRuleForm.get('organization').setValue(this.organizations[0]);
        } else {
          this.cepRuleForm.get('organization').enable();
        }
      });
    }
  }

  private loadOrganizations(tenantUuid: string): Promise<any> {
    return Promise.all([
      this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise()
    ]).then(response => {
      this.organizations = response[0].organizations;
    });
  }

  selectRules(selectedRules: SimpleCepRule[]): void {
    this.selectedRule = selectedRules[0];
    this.selectedEntities = [];
    this.hasBeenOpened = false;
    if (this.selectedRule.entitiesAndSensorIdentifiers?.length > 0) {
      this.loadSelectedFromIdentifiers(this.selectedRule.entitiesAndSensorIdentifiers);
    }
  }

  updateHasBeenOpened(event) {
    this.hasBeenOpened = event;
  }

  addSimpleRule(): void {
    this.selectedEntities = [];
    const newSimpleRule = new SimpleCepRule(this.cepRuleForm.get('nameComplexRule').value.concat("_", (this.indexName++).toString()),
      [], 0, 10);
    this.simpleRules.push(newSimpleRule);
    const swap = this.simpleRules;
    this.simpleRules = undefined;
    setTimeout(() => {
      this.simpleRules = swap;
      this.selectedRule = this.simpleRules[this.simpleRules.length - 1];
    }, 0);
  }

  removeSimpleRule(): void {
    const index = this.simpleRules.indexOf(this.selectedRule);
    this.simpleRules.splice(index, 1);
    const swap = this.simpleRules;
    this.simpleRules = undefined;
    setTimeout(() => {
      this.simpleRules = swap;
      const lastItem = (index === this.simpleRules.length) ? 1 : 0;
      if (this.simpleRules.length !== 0) {
        this.selectRules([this.simpleRules[index - lastItem]]);
      } else {
        this.selectedRule = undefined;
        this.selectedEntities = [];
      }
    }, 0);
  }

  public deselectEntity(entity: SelectedEntity): void {
    this.selectedEntities = this.selectedEntities.filter(e => e.uuid !== entity.uuid);
    this.refreshEntities();
  }

  public refreshEntities(): void {
    this.selectedRule.entitiesAndSensorIdentifiers = [];
    this.selectedEntities.forEach((row: SelectedEntity) => {
      this.selectedRule.entitiesAndSensorIdentifiers.push({
        entityType: row.entityType,
        entityUuid: row.uuid,
        entityIdentifier: row.identifier,
        measurementName: row.measurementName,
        measurementTypeUnit: row.measurementTypeUnit
      });
    });
  }

  private loadSelectedFromIdentifiers(entitiesAndSensorIdentifiers: EntitiesAndSensorIdentifier[]): void {
    this.loadingSelected = true;
    entitiesAndSensorIdentifiers.forEach((identifier: any) => {
      this.selectedEntities.push({
        uuid: identifier.entityUuid,
        entityType: identifier.entityType,
        identifier: identifier.entityIdentifier,
        measurementName: identifier.measurementName,
        measurementTypeUnit: identifier.measurementTypeUnit
      });
      this.loadingSelected = false;
    });
  }

  onTenantChange(event): void {
    if (event.value) {
      if(this.simpleRules.length === 0) {
        this.onTenantChangeAction(event);
      } else {
        this.commonMessagesService.showDeleteSimpleRulesConfirmation(() => {
            this.onTenantChangeAction(event);
          }, () => {
            this.cepRuleForm.get('tenant').setValue(this.cepRule.tenant);
          });
      }
    }
  }

  onTenantChangeAction(event): void {
    this.cepRuleForm.get('organization').setValue(null);
    this.loadOrganizations(event.value.uuid);
    this.cepRuleForm.get('organization').enable();
    this.cepRule.tenant = this.cepRuleForm.get('tenant').value;
    this.cepRule.organization = null;
    this.resetValues();
  }

  onOrganizationChange(): void {
    if(this.simpleRules.length === 0) {
      this.onOrganizationChangeAction();
    } else {
      this.commonMessagesService.showDeleteSimpleRulesConfirmation(() => {
          this.onOrganizationChangeAction();
        }, () => {
          this.cepRuleForm.get('organization').setValue(this.cepRule.organization);
        });
    }
  }

  onOrganizationChangeAction(): void {
    this.cepRule.organization = this.cepRuleForm.get('organization').value;
    this.resetValues();
  }

  onNameComplexRuleChange(): void {
    this.simpleRules.forEach((simpleRule: SimpleCepRule, i) => {
      simpleRule.name = this.cepRuleForm.get('nameComplexRule').value.concat("_", (i+1).toString());
    });
  }

  resetValues(): void {
    this.simpleRules = [];
    this.selectedRule = undefined;
    this.selectedEntities = [];
    this.indexName = 1;
  }

  persistCepRuleData(): void {
    this.persistingChanges = true;
    const formValue = this.cepRuleForm.getRawValue();
    if(this.mode === 'create') {
      this.tenantsService.getExistsTenantCepRule(formValue.tenant.uuid, formValue.organization ? formValue.organization.uuid : null,
        formValue.nameComplexRule).toPromise()
        .then(response => {
          if(response.exists == true) {
            this.commonMessagesService.showEntityAlreadyExistsError(this.mode, this.cepRuleForm.get('nameComplexRule').value);
            this.persistingChanges = false;
          } else {
            this.persistCepRuleData2();
          }
        });
    } else {
      this.persistCepRuleData2();
    }
  }

  private persistCepRuleData2(): void {
    let persistData = this.buildPersistData();
    const promise = (this.mode === 'create') ?
      this.cepRulesService.createCepRule(persistData).toPromise() :
      this.cepRulesService.editCepRule(persistData).toPromise();

    promise.then(() => {
      this.formSuccess = true;
      this.router.navigate([`/alarms/cep-rules`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, this.cepRuleForm.get('nameComplexRule').value);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): CepRulePersistRequest {
    const formValue = this.cepRuleForm.getRawValue();
    return {
      nameComplexRule: formValue.nameComplexRule,
      tenantUuid: formValue.tenant.uuid,
      organizationUuid: formValue.organization ? formValue.organization.uuid : null,
      simpleRules: this.simpleRules
    };
  }

  isFormValid() : boolean {
    return this.cepRuleForm.disabled ? true : this.cepRuleForm.valid;
  }

  validSimpleRules(): boolean {
    if (this.simpleRules === undefined || this.simpleRules.length === 0) {
      return false;
    }
    for (let simpleRule of this.simpleRules) {
      if (simpleRule.name === undefined || simpleRule.entitiesAndSensorIdentifiers === undefined || simpleRule.limitValue === undefined ||
        simpleRule.aggregatorFunctionType === undefined || simpleRule.limitOperatorType === undefined ||
        simpleRule.windowTime === undefined || simpleRule.windowTime <= 0 || simpleRule.entitiesAndSensorIdentifiers.length === 0) {
        return false;
      }
    }
    return true;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.cepRuleForm.untouched;
  }
}
